/**
 * ShopifyIntegrationSettings — הגדרות ממשק שופיפיי
 *
 * Per-integration settings page for Shopify (shopify_manual provider).
 * Settings stored in company_integrations.settings (JSONB).
 * Sync schedules stored in integration_sync_jobs (one row per sync type).
 *
 * Phase 95: replaced global sync_frequency with per-action frequency controls.
 *
 * Route: /app/settings/integrations/shopify
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, RefreshCw, ShoppingCart, Tag, Zap, ArrowUpDown, ArrowDownUp, ArchiveX, Play, Loader as Loader2, Clock, CircleCheck as CheckCircle2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useCompany } from '@/context/CompanyContext';
import { SettingsTabs } from '@/components/settings';
import {
  IntegrationSettingsLayout,
  SettingsSection,
  ToggleRow,
  SelectRow,
  type IntegrationInfo,
} from './IntegrationSettingsLayout';

// ─── Types ────────────────────────────────────────────────────────────────────

type SyncFrequency = 'manual' | 'hourly' | 'every6h' | 'daily';
type SyncType      = 'inventory' | 'orders' | 'status' | 'prices';

interface SyncJob {
  id:               string;
  sync_type:        SyncType;
  frequency:        SyncFrequency;
  is_enabled:       boolean;
  last_run_at:      string | null;
  last_run_status:  'ok' | 'error' | null;
  last_run_summary: Record<string, unknown> | null;
  next_run_at:      string | null;
}

interface ShopifySettings {
  // Axelio → Shopify
  sync_inventory:           boolean;
  sync_prices:              boolean;
  sync_orders:              boolean;
  auto_import_new_products: boolean;
  update_shopify_on_change: boolean;
  default_price_list:       string;
  // Shopify → Axelio (Phase 94)
  pull_stock_status:        boolean;
  alert_on_archived:        boolean;
  auto_deactivate_archived: boolean;
}

const DEFAULT_SETTINGS: ShopifySettings = {
  sync_inventory:           true,
  sync_prices:              false,
  sync_orders:              true,
  auto_import_new_products: false,
  update_shopify_on_change: false,
  default_price_list:       'catalog',
  pull_stock_status:        false,
  alert_on_archived:        false,
  auto_deactivate_archived: false,
};

const FREQ_OPTIONS = [
  { value: 'manual',  label: 'ידני בלבד' },
  { value: 'hourly',  label: 'כל שעה' },
  { value: 'every6h', label: 'כל 6 שעות' },
  { value: 'daily',   label: 'פעם ביום' },
];

const fmtDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('he-IL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

// ─── Sub-component: SyncJobRow ─────────────────────────────────────────────────
// Shows last-run badge + frequency selector for one sync type.

interface SyncJobRowProps {
  label:       string;
  description: string;
  job:         SyncJob | null;
  onChange:    (freq: SyncFrequency, enabled: boolean) => void;
  onRunNow:    () => void;
  running:     boolean;
  disabled?:   boolean;
}

function SyncJobRow({ label, description, job, onChange, onRunNow, running, disabled }: SyncJobRowProps) {
  const freq    = job?.frequency    ?? 'manual';
  const enabled = job?.is_enabled   ?? false;
  const lastRun = job?.last_run_at  ?? null;
  const status  = job?.last_run_status ?? null;

  return (
    <div className={`py-3 border-b border-gray-50 last:border-0 space-y-2 ${disabled ? 'opacity-50' : ''}`}>
      {/* Top row: toggle + label */}
      <label className="flex items-start justify-between gap-4 cursor-pointer group">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-800 group-hover:text-gray-900 transition-colors">
            {label}
          </p>
          <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{description}</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          disabled={disabled}
          onClick={() => !disabled && onChange(freq, !enabled)}
          className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full
                      transition-colors focus:outline-none
                      ${enabled ? 'bg-primary-600' : 'bg-gray-200'}
                      ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform
                            ${enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
        </button>
      </label>

      {/* Bottom row: frequency selector + last run badge + run now */}
      {enabled && (
        <div className="flex items-center gap-3 flex-wrap pr-1">
          {/* Frequency dropdown */}
          <select
            value={freq}
            onChange={e => onChange(e.target.value as SyncFrequency, true)}
            disabled={disabled}
            className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5
                       text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary-400
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {FREQ_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          {/* Last run badge */}
          {lastRun && (
            <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-full
              ${status === 'error'
                ? 'bg-red-50 text-red-600 border border-red-200'
                : 'bg-gray-50 text-gray-500 border border-gray-200'}`}>
              {status === 'error'
                ? <ArchiveX className="w-2.5 h-2.5" />
                : <Clock className="w-2.5 h-2.5" />}
              {fmtDateTime(lastRun)}
            </span>
          )}

          {/* Run now button */}
          <button
            onClick={onRunNow}
            disabled={running || disabled}
            className="inline-flex items-center gap-1 text-[10px] font-semibold
                       px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-gray-600
                       rounded-lg transition-colors disabled:opacity-50"
          >
            {running
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <Play className="w-3 h-3" />}
            {running ? 'מסנכרן…' : 'סנכרן עכשיו'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ShopifyIntegrationSettings() {
  const { activeCompanyId } = useCompany();
  const navigate = useNavigate();

  const [integration, setIntegration] = useState<IntegrationInfo | null>(null);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState<string | null>(null);
  const [toast,       setToast]       = useState<string | null>(null);
  const [settings,    setSettings]    = useState<ShopifySettings>(DEFAULT_SETTINGS);

  // Sync jobs map: sync_type → SyncJob
  const [syncJobs,    setSyncJobs]    = useState<Map<SyncType, SyncJob>>(new Map());
  // Which sync type is currently running a manual trigger
  const [runningSync, setRunningSync] = useState<SyncType | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 5000);
  };

  // ── Load integration + sync jobs ──────────────────────────────────────────

  const load = useCallback(async () => {
    if (!activeCompanyId) return;
    setLoading(true); setError(null);
    try {
      const { data, error: e } = await supabase
        .from('company_integrations')
        .select('id, provider_name, credentials, settings, is_active, last_sync_at')
        .eq('company_id', activeCompanyId)
        .eq('provider_name', 'shopify_manual')
        .maybeSingle();

      if (e) throw e;
      if (!data) {
        navigate('/settings/integrations', { replace: true });
        return;
      }

      setIntegration(data as IntegrationInfo);
      const stored = (data.settings ?? {}) as Partial<ShopifySettings>;
      setSettings({ ...DEFAULT_SETTINGS, ...stored });

      // Load sync jobs
      const { data: jobs } = await supabase
        .from('integration_sync_jobs')
        .select('id, sync_type, frequency, is_enabled, last_run_at, last_run_status, last_run_summary, next_run_at')
        .eq('company_id', activeCompanyId)
        .eq('integration_id', data.id);

      const map = new Map<SyncType, SyncJob>();
      for (const j of (jobs ?? [])) {
        map.set(j.sync_type as SyncType, j as SyncJob);
      }
      setSyncJobs(map);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'שגיאה בטעינת הגדרות');
    } finally {
      setLoading(false);
    }
  }, [activeCompanyId, navigate]);

  useEffect(() => { load(); }, [load]);

  // ── Save settings ─────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!activeCompanyId || !integration) return;
    setSaving(true); setError(null);
    try {
      const { error: e } = await supabase
        .from('company_integrations')
        .update({ settings })
        .eq('id', integration.id);
      if (e) throw e;
      showToast('הגדרות שמורות בהצלחה');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'שגיאה בשמירת הגדרות');
    } finally {
      setSaving(false);
    }
  };

  const set = <K extends keyof ShopifySettings>(key: K, val: ShopifySettings[K]) =>
    setSettings(prev => ({ ...prev, [key]: val }));

  // ── Upsert a sync job row ─────────────────────────────────────────────────

  const upsertSyncJob = useCallback(async (
    syncType: SyncType,
    frequency: SyncFrequency,
    isEnabled: boolean,
  ) => {
    if (!activeCompanyId || !integration) return;
    const { data, error: e } = await supabase.rpc('upsert_sync_job', {
      p_company_id:     activeCompanyId,
      p_integration_id: integration.id,
      p_sync_type:      syncType,
      p_frequency:      frequency,
      p_is_enabled:     isEnabled,
    });
    if (e) { setError(e.message); return; }
    if (data) {
      setSyncJobs(prev => new Map(prev).set(syncType, data as SyncJob));
    }
  }, [activeCompanyId, integration]);

  // ── Run a specific sync type now via orchestrator ─────────────────────────

  const handleRunNow = useCallback(async (syncType: SyncType) => {
    if (!activeCompanyId) return;
    setRunningSync(syncType);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('לא מחובר');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
      const res = await fetch(`${supabaseUrl}/functions/v1/shopify-sync-orchestrator`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ company_id: activeCompanyId, sync_type: syncType }),
      });
      const json = await res.json() as {
        processed?: number;
        results?:   Array<{ status: string; summary: Record<string, unknown> }>;
        error?:     string;
      };
      if (!res.ok) throw new Error(json.error ?? `HTTP ${res.status}`);

      const result = json.results?.[0];
      if (result?.status === 'error') {
        const errMsg = (result.summary as { error?: string })?.error ?? 'שגיאה לא ידועה';
        throw new Error(errMsg);
      }

      showToast(`סנכרון "${syncType}" הושלם בהצלחה`);
      // Refresh job row to update last_run_at
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'שגיאה בסנכרון');
    } finally {
      setRunningSync(null);
    }
  }, [activeCompanyId, load]);

  // ── Render ────────────────────────────────────────────────────────────────

  const job = (t: SyncType) => syncJobs.get(t) ?? null;

  return (
    <div className="max-w-5xl mx-auto space-y-6" dir="rtl">
      <SettingsTabs />

      {toast && (
        <div className="flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200
                        rounded-xl text-sm text-emerald-700">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          {toast}
        </div>
      )}

      <IntegrationSettingsLayout
        providerLabel="Shopify (חיבור ידני)"
        logoText="🛍️"
        logoBg="bg-green-50"
        integration={integration}
        loading={loading}
        saving={saving}
        error={error}
        onSave={handleSave}
        onEditCredentials={() => navigate('/settings/integrations')}
      >

        {/* ── Axelio → Shopify: Inventory ── */}
        <SettingsSection
          title="סנכרון מלאי — Axelio לשופיפיי"
          description="שליטה על עדכון יתרות המלאי בשופיפיי על פי נתוני Axelio"
          icon={<Package className="w-4 h-4 text-green-600" />}
        >
          <ToggleRow
            label="עדכן שופיפיי בעת אישור מסמך (real-time)"
            description="בכל פעם שמסמך מלאי מאושר ב-Axelio, שולח עדכון מיידי ל-Shopify. מומלץ לחנויות פעילות."
            checked={settings.update_shopify_on_change}
            onChange={v => set('update_shopify_on_change', v)}
            disabled={!settings.sync_inventory}
          />
          <SyncJobRow
            label="סנכרון יתרות מלאי תזמוני"
            description="דחיפת יתרות מלאי מ-Axelio לשופיפיי לפי תדירות. מומלץ: כל שעה."
            job={job('inventory')}
            onChange={(freq, enabled) => {
              set('sync_inventory', enabled);
              upsertSyncJob('inventory', freq, enabled);
            }}
            onRunNow={() => handleRunNow('inventory')}
            running={runningSync === 'inventory'}
          />
        </SettingsSection>

        {/* ── Axelio → Shopify: Prices ── */}
        <SettingsSection
          title="סנכרון מחירים — Axelio לשופיפיי"
          description="שליטה על סנכרון מחירי מוצרים לשופיפיי"
          icon={<Tag className="w-4 h-4 text-amber-600" />}
        >
          <SelectRow
            label="מחירון ברירת מחדל לסנכרון"
            description="איזה מחירון של Axelio ישמש לסנכרון המחירים לשופיפיי."
            value={settings.default_price_list}
            options={[
              { value: 'catalog', label: 'מחיר קטלוג (ברירת מחדל)' },
              { value: 'b2b',     label: 'מחיר B2B' },
              { value: 'cost',    label: 'מחיר עלות' },
            ]}
            onChange={v => set('default_price_list', v)}
            disabled={!settings.sync_prices}
          />
          <SyncJobRow
            label="סנכרון מחירים תזמוני"
            description="דחיפת מחירים מ-Axelio לשופיפיי. מומלץ: כל 6 שעות (שינויי מחיר אינם דחופים)."
            job={job('prices')}
            onChange={(freq, enabled) => {
              set('sync_prices', enabled);
              upsertSyncJob('prices', freq, enabled);
            }}
            onRunNow={() => handleRunNow('prices')}
            running={runningSync === 'prices'}
          />
        </SettingsSection>

        {/* ── Shopify → Axelio: Orders ── */}
        <SettingsSection
          title="משיכת הזמנות — שופיפיי ל-Axelio"
          description="הזמנות מהחנות מועברות ל-Axelio לטיפול ומשלוח"
          icon={<ShoppingCart className="w-4 h-4 text-primary-600" />}
        >
          <SyncJobRow
            label="משיכת הזמנות חדשות תזמונית"
            description="שולף הזמנות פתוחות מ-Shopify ומכניס אותן לתור הייבוא. מומלץ: כל שעה."
            job={job('orders')}
            onChange={(freq, enabled) => {
              set('sync_orders', enabled);
              upsertSyncJob('orders', freq, enabled);
            }}
            onRunNow={() => handleRunNow('orders')}
            running={runningSync === 'orders'}
          />
        </SettingsSection>

        {/* ── Shopify → Axelio: Status ── */}
        <SettingsSection
          title="קבלת מצב מוצרים — שופיפיי ל-Axelio"
          description="סנכרון מידע על סטטוס ומלאי מוצרים מהחנות — אינפורמטיבי בלבד"
          icon={<ArrowDownUp className="w-4 h-4 text-teal-600" />}
        >
          <ToggleRow
            label="הצג חיווי מלאי קיים / אזל בעמוד המוצרים"
            description="badge תצוגתי בלבד על בסיס כמות המלאי בשופיפיי. אינו משפיע על חישובי המלאי ב-Axelio."
            checked={settings.pull_stock_status}
            onChange={v => set('pull_stock_status', v)}
          />
          <ToggleRow
            label="התרא כאשר מוצרים הופכים ל-Archived בשופיפיי"
            description="toast בכניסה לעמוד המוצרים כשיש מוצרים שהפכו ל-archived מאז הסנכרון האחרון."
            checked={settings.alert_on_archived}
            onChange={v => set('alert_on_archived', v)}
          />
          <ToggleRow
            label="בקש אישור לבטל מוצר ב-Axelio כשהוא מוסר בשופיפיי"
            description="שאלת אישור מוצגת כאשר מוצר מסומן כ-archived בשופיפיי. ניתן לדחות ולהשאיר פעיל."
            checked={settings.auto_deactivate_archived}
            onChange={v => set('auto_deactivate_archived', v)}
            disabled={!settings.alert_on_archived}
          />
          {(settings.pull_stock_status || settings.alert_on_archived) && (
            <SyncJobRow
              label="בדיקת סטטוס מוצרים תזמונית"
              description="שולף סטטוס ומלאי כל המוצרים מ-Shopify ומעדכן את השדות האינפורמטיביים. מומלץ: כל 6 שעות."
              job={job('status')}
              onChange={(freq, enabled) => upsertSyncJob('status', freq, enabled)}
              onRunNow={() => handleRunNow('status')}
              running={runningSync === 'status'}
            />
          )}
        </SettingsSection>

        {/* ── Product Import (manual only) ── */}
        <SettingsSection
          title="ייבוא מוצרים"
          description="ייבוא מוצרים מהחנות לקטלוג — מבוצע ידנית בלבד"
          icon={<ArrowUpDown className="w-4 h-4 text-blue-600" />}
        >
          <div className="flex items-start gap-3 px-1 py-2">
            <RefreshCw className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-gray-700">ייבוא מוצרים — ידני בלבד</p>
              <p className="text-xs text-gray-400 mt-0.5">
                ייבוא מוצרים מ-Shopify לקטלוג Axelio אינו מתבצע אוטומטית.
                ניתן לבצע ייבוא ידני בכל עת מעמוד ייבוא וסנכרון.
              </p>
            </div>
          </div>
        </SettingsSection>

        {/* ── Info footer ── */}
        <div className="flex items-start gap-3 px-4 py-3 bg-blue-50 border border-blue-100 rounded-xl text-xs text-blue-700">
          <Zap className="w-3.5 h-3.5 shrink-0 mt-0.5 text-blue-500" />
          <p>
            <strong>שים לב:</strong> שינויי הגדרות נכנסים לתוקף בסנכרון הבא.
            כל פעולת סנכרון זמינה גם ידנית בלחיצה על "סנכרן עכשיו" ליד כל סעיף.
          </p>
        </div>

      </IntegrationSettingsLayout>
    </div>
  );
}
